import {
    FC,
    ReactNode,
    useState,
    useCallback,
    useEffect,
    ComponentProps,
    MouseEventHandler,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import slugify from 'dibs-slugify/exports/slugify';
import { trackEvent } from 'dibs-tracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { Link } from 'dibs-elements/exports/Link';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { useTab } from 'dibs-elements/exports/useTab';
import { PdpRespStorytellingTab } from '../PdpRespStorytellingModule/PdpRespStorytellingTab';
import {
    PdpSharedStorytellingTrackingContextProvider,
    PdpSharedStorytellingTrackingWrapper,
} from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';

import { PdpRespAboutItemPanel } from './PdpRespAboutItemPanel';

import { PdpRespAboutTabs_viewer$key } from './__generated__/PdpRespAboutTabs_viewer.graphql';
import { PdpRespAboutTabs_user$key } from './__generated__/PdpRespAboutTabs_user.graphql';
import {
    PdpRespAboutTabs_item$data,
    PdpRespAboutTabs_item$key,
} from './__generated__/PdpRespAboutTabs_item.graphql';

import styles from './PdpRespAboutTabs.scss';

type Tabs = NonNullable<
    NonNullable<NonNullable<PdpRespAboutTabs_item$data>['storyTellingModule']>['tabs']
>;

const ABOUT_ITEM = 'ABOUT_ITEM';

const labelMap: Record<string, string> = {
    [ABOUT_ITEM]: 'about the item',
    'iconic design': 'about the design',
    creator: 'creator',
    'seller-maker': 'about the maker',
};

const CustomTab: FC<{ name: string; type: string; position?: number; dataTn?: string }> = ({
    name,
    type,
    position,
    dataTn,
    children,
}) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);

    const getTrackingObject = useCallback(
        (action: string) => {
            // Shouldn't ever be used, but adding fallback to identify if unexpected tab type is ever encountered that doesn't exist in label map
            let label = labelMap[type] || `unexpected ${type}`;

            if (position) {
                label += ` - ${position}`;
            }

            return {
                category: 'product',
                action,
                label,
            };
        },
        [type, position]
    );

    // Track tab impression on mount
    useEffect(() => {
        trackEvent(getTrackingObject('pdp tab display'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();
            trackEvent(getTrackingObject('pdp tab clicked'), event);
        },
        [handleClick, getTrackingObject]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                buyerLinkType="linkNoDecoration"
                linkRef={tabCallbackRef}
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
                onClick={onClick}
                dataTn={dataTn}
            >
                {children}
            </Link>
        </li>
    );
};

const AboutTabsComponent: FC<ComponentProps<typeof PdpRespAboutItemPanel> & { tabs: Tabs }> = ({
    tabs,
    ...props
}) => {
    const [activeTab, setActiveTab] = useState(ABOUT_ITEM);
    let numCreatorTabs = 0;
    return (
        <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
            <div className={styles.tabListWrapper}>
                <TabList fullWidth customTab indicatorPadding="xSmall">
                    {[
                        [
                            ABOUT_ITEM,
                            <CustomTab
                                key={ABOUT_ITEM}
                                name={ABOUT_ITEM}
                                type={ABOUT_ITEM}
                                dataTn="item-details-tab"
                            >
                                <FormattedMessage
                                    id="PdpRespAboutTabs.itemDetails"
                                    defaultMessage="About the Item"
                                />
                            </CustomTab>,
                        ],
                        ...tabs.filter(filterNulls).map<[string, ReactNode]>(tab => {
                            // Position is only incremented and passed for 'creator' tabTypes
                            let position: number | undefined = undefined;
                            if (tab.tabType === 'creator') {
                                numCreatorTabs += 1;
                                position = numCreatorTabs;
                            }
                            return [
                                tab.tabTitle,
                                <CustomTab
                                    key={tab.tabTitle}
                                    name={tab.tabTitle}
                                    type={tab.tabType}
                                    position={position}
                                    dataTn={`${slugify(tab.tabTitle)}-tab`}
                                >
                                    {tab.tabTitle}
                                </CustomTab>,
                            ];
                        }),
                    ]}
                </TabList>
            </div>

            <div className={styles.panelsWrapper}>
                <PdpSharedStorytellingTrackingContextProvider>
                    <TabPanels renderOnInactive>
                        {[
                            [
                                ABOUT_ITEM,
                                <PdpRespAboutItemPanel
                                    {...props}
                                    showHeader={false}
                                    showExpandingArea={activeTab === ABOUT_ITEM}
                                />,
                            ],
                            ...tabs.filter(filterNulls).map<[string, ReactNode]>(tab => [
                                tab.tabTitle,
                                <PdpSharedStorytellingTrackingWrapper key={tab.tabTitle}>
                                    <PdpRespStorytellingTab
                                        showHeader={false}
                                        wrapperClass={styles.storyTellingWrapper}
                                        storyTellingTab={tab}
                                        clearBackground={false}
                                        //Because of the current inactive tab rendering we need this workaraound to make expanding area functional.
                                        showExpandingArea={activeTab === tab.tabTitle}
                                        dataTn={`${slugify(tab.tabTitle)}-tab-content`}
                                    />
                                </PdpSharedStorytellingTrackingWrapper>,
                            ]),
                        ]}
                    </TabPanels>
                </PdpSharedStorytellingTrackingContextProvider>
            </div>
        </TabProvider>
    );
};

const viewerFragment = graphql`
    fragment PdpRespAboutTabs_viewer on Viewer {
        ...PdpRespAboutItemPanel_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutTabs_user on User {
        ...PdpRespAboutItemPanel_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespAboutTabs_item on Item {
        skus {
            serviceId
        }
        storyTellingModule {
            tabs {
                tabType
                tabTitle
                ...PdpRespStorytellingTab_storyTellingTab
            }
        }
        ...PdpRespAboutItemPanel_item
    }
`;

export const PdpRespAboutTabs: FC<{
    selectedSkuId: string | null;
    viewer: PdpRespAboutTabs_viewer$key;
    user: PdpRespAboutTabs_user$key;
    item: PdpRespAboutTabs_item$key;
}> = ({ selectedSkuId, viewer: viewerRef, user: userRef, item: itemRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const tabs = item?.storyTellingModule?.tabs || [];
    const hasTabs = tabs.length > 0;

    return (
        <div className={styles.container}>
            <CurrencyProvider
                render={({ currency }: $TSFixMe) => {
                    const aboutItemPanelProps = {
                        item,
                        user,
                        currency,
                        selectedSkuId,
                        viewer,
                    };

                    return (
                        <div className={styles.innerContainer}>
                            {hasTabs ? (
                                <AboutTabsComponent tabs={tabs} {...aboutItemPanelProps} />
                            ) : (
                                <PdpRespAboutItemPanel showHeader {...aboutItemPanelProps} />
                            )}
                        </div>
                    );
                }}
            />
        </div>
    );
};
